<template>
  <div v-if="haveVettingBookingRequest">
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <booking-campaign-banner v-if="booking && booking.campaign" :campaign="booking.campaign" />
        <div class="row">
          <div class="col-md-6">
            <h1>{{ isDraftVettingRequest ? 'Draft' : 'Pending' }} {{ $t('Request') }} #{{ booking.id | padded('0', 5) }}</h1>
          </div>
          <div class="col-md-6">
            <booking-status
              :booking="booking"/>
          </div>
        </div>

      </div>
    </div>

    <div class="container-fluid">
      <booking-search-details-list-admin
        :booking="booking"
        v-if="$userIsAdmin"/>

      <booking-progress
        v-if="$userIsClient"
        :booking="booking"/>

      <booking-details
        :booking="booking"
        :property-mutator="setVettingRequestProperty"/>

      <booking-client-details
        v-if="($userIsAdmin || $userIsClient) || (($userIsTalent || $userIsManager) && $showClientDetailsBookingModuleEnabled)"
        :booking="booking"/>

      <booking-tag-targeting
        v-if="canViewRegionalAdminOptions && $billboardModuleEnabled" :booking="booking"/>

      <booking-notes
        v-if="$userIsAdmin"
        :booking="booking"/>

      <div v-if="!$vlastMode">
        <booking-source
          v-if="canViewRegionalAdminOptions"
          :booking="booking"/>
      </div>

      <booking-sales-pipeline key="1" v-if="$userIsAdmin && !$vlastMode" :booking="booking"/>

      <request-admin-options
        v-if="(!$vlastMode || (monetaryCurrency && booking.budget_per_star > 0)) && canViewRegionalAdminOptions"
        :booking="booking"
        :property-mutator="setVettingRequestProperty"/>

      <booking-history
        v-if="$userIsAdmin" :booking="booking">
      </booking-history>

      <booking-search-details-list
        :booking="booking"
        v-if="$userIsClient"/>

      <booking-download-pdf
        v-if="$userIsAdmin"
        :booking="booking"/>

      <booking-upload-files
        v-if="$userIsAdmin && booking.can_upload_files"
        :booking="booking"/>

      <div class="dashboard-block dashboard-block__actions" v-if="canViewRegionalAdminOptions">
        <h2>Actions</h2>

        <div v-if="isArchiveDraftRequest">
          <ladda-button
            class="btn btn-success"
            :click="handleRestoreDraftRequestCallback">
            <i class="fa fa-check"></i> Restore Draft
          </ladda-button>
        </div>
        <div v-else-if="isDraftVettingRequest">
          <ladda-button
            class="btn btn-success"
            :click="handleApproveDraftRequestCallback">
            <i class="fa fa-check"></i> Approve Draft
          </ladda-button>
          <ladda-button
            v-if="!$vlastMode"
            class="btn btn-delete"
            :click="handleArchiveDraftRequestCallback">
            <i class="fa fa-check"></i> Archive Draft
          </ladda-button>
        </div>
        <div v-else>
          <ladda-button
            class="btn btn-success"
            :click="handleApproveRequestCallback">
            <i class="fa fa-check"></i> Approve {{ $t('Request') }}
          </ladda-button>
        </div>

        <ladda-button
          v-if="!isDraftVettingRequest"
          class="btn btn-danger pull-right"
          :click="handleDeclineRequestCallback">
          <i class="fa fa-close"></i> Cancel {{ $t('Request') }}
        </ladda-button>
      </div>
      <div class="dashboard-block dashboard-block__actions" v-if="canViewRegionalAdminOptions">
        <router-link :to="{ name: 'vetting.edit', params: { id } }" class="btn btn-info">
          <i class="fa fa-pencil"></i> Edit {{ $t('Request') }}
        </router-link>
        <router-link :to="{ name: 'vetting.duplicate', params: { id } }" class="btn btn-info" v-if="!isDraftVettingRequest">
          <i class="fa fa-clone"></i> &nbsp; Duplicate {{ $t('Request') }}
        </router-link>
      </div>
      <vetting-decline-reason-modal
        ref="modal"
        @input="handleDeclineRequestReason"/>
      <booking-terms-and-conditions-modal-no-action
        v-if="($userIsAdmin || $userIsClient)"
        :booking="booking"/>
    </div>
  </div>
</template>

<script>
import { OpportunityTracker } from 'mixins/analytics'
import { mapGetters, mapActions, mapState } from 'vuex'
import { notify, alert } from 'utils'
import BookingProgress from 'components/booking/BookingProgress'
import BookingDetails from 'components/booking/BookingDetails'
import BookingStatus from 'components/booking/BookingStatus'
import BookingClientDetails from 'components/booking/BookingClientDetails'
import BookingSearchDetailsList from 'components/booking/BookingSearchDetailsList'
import BookingSearchDetailsListAdmin from 'components/booking/BookingSearchDetailsListAdmin'
import BookingNotes from 'components/booking/BookingNotes'
import BookingSource from 'components/booking/BookingSource'
import BookingTermsAndConditionsModalNoAction from 'components/booking/BookingTermsAndConditionsModalNoAction'
import VettingDeclineReasonModal from 'components/booking/vetting/VettingDeclineReasonModal'
import RequestAdminOptions from 'components/booking/request/RequestAdminOptions'
import BookingDownloadPdf from 'components/booking/BookingDownloadPdf'
import BookingTagTargeting from 'components/booking/BookingTagTargeting'
import BookingHistory from 'components/booking/BookingHistory'
import BookingUploadFiles from '../BookingUploadFiles'
import BookingSalesPipeline from '../BookingSalesPipeline'
import BookingCampaignBanner from '../BookingCampaignBanner'

export default {
  props: ['id'],

  mixins: [OpportunityTracker],

  data () {
    return {
      withdrawRequestPromise: null,
      withdrawRequestReason: null
    }
  },

  components: {
    BookingCampaignBanner,
    BookingSalesPipeline,
    BookingHistory,
    BookingProgress,
    BookingDetails,
    BookingStatus,
    BookingClientDetails,
    BookingSearchDetailsList,
    BookingSearchDetailsListAdmin,
    BookingNotes,
    BookingSource,
    BookingTermsAndConditionsModalNoAction,
    VettingDeclineReasonModal,
    RequestAdminOptions,
    BookingDownloadPdf,
    BookingUploadFiles,
    BookingTagTargeting
  },

  computed: {
    ...mapGetters('booking/request/vetting', ['haveVettingBookingRequest']),
    ...mapState('booking/request/vetting', {
      booking: 'request'
    }),

    isDraftVettingRequest () {
      return this.booking.type === 'draft'
    },

    isArchiveDraftRequest () {
      return this.booking.type === 'draft' && this.booking.status === 'archived'
    },

    canViewRegionalAdminOptions () {
      return this.$userIsBookingRegionalAdmin(this.$user, this.booking)
    }
  },

  destroyed () {
    this.resetVettingRequest()
    this.clearAllFilters()
  },

  mounted () {
    this.getVettingBookingRequest({ id: this.id })
      .then(() => {
        this.trackEvent(this.analytics.V2.JobOpenEvent)
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          notify.error('The pending ' + this.$t('request') + ' could not be found.')

          this.$router.push({ name: 'dashboard', query: { src: 'vetting-single' } })

          return
        }

        throw error
      })
  },

  methods: {
    ...mapActions('booking/request/vetting', [
      'getVettingBookingRequest',
      'approveVettingBookingRequest',
      'declineVettingBookingRequest',
      'approveDraftBookingRequest',
      'restoreDraftBookingRequest',
      'resetVettingRequest',
      'setVettingRequestProperty',
      'archiveDraftBookingRequest'
    ]),
    ...mapActions('filtering', ['clearAllFilters']),

    /**
     * Handle approving a vetting request.
     *
     * @return Promise
     */
    handleApproveRequestCallback () {
      return this.approveVettingBookingRequest({ id: this.booking.id })
        .then(() => {
          return alert.timedSuccess({
            buttons: {
              confirm: {
                text: 'Continue'
              }
            },
            timer: 3000,
            title: 'Success!',
            text: 'Pending ' + this.$t('request') + ' has been approved.'
          })
        })
        .then(this.handleRedirectToRequest)
    },

    /**
     * Handle approve a draft request. (Transforms into Vetting Request)
     *
     * @return Promise
     */
    handleApproveDraftRequestCallback () {
      return this.approveDraftBookingRequest({ id: this.booking.id }).then(this.handleRedirectToRequest)
    },

    /**
     * Handle archive a draft request.
     *
     * @return Promise
     */
    handleArchiveDraftRequestCallback () {
      return this.archiveDraftBookingRequest({ id: this.booking.id })
    },

    /**
     * Handle approve a draft request. (Transforms into Vetting Request)
     *
     * @return Promise
     */
    handleRestoreDraftRequestCallback () {
      return this.restoreDraftBookingRequest({ id: this.booking.id }).then(this.handleRedirectToRequest)
    },

    /**
     * Handle declining a vetting request.
     *
     * @return Promise
     */
    handleDeclineRequestCallback () {
      this.declineRequestReason = null

      const confirmAlertOptions = {
        title: 'Decline Pending ' + this.$t('request'),
        text: 'Are you sure you want to decline this pending ' + this.$t('request') + '?'
      }

      return alert.confirm(confirmAlertOptions)
        .then(() => {
          this.withdrawRequestReason = null
          window.$(this.$refs.modal.$el).modal('show')
        })
    },

    handleDeclineRequestReason (reason) {
      this.declineRequestReason = reason

      this.declineVettingBookingRequest({ id: this.booking.id, reason: this.declineRequestReason })
        .then(() => {
          return alert.timedSuccess({
            buttons: {
              confirm: {
                text: 'Continue'
              }
            },
            timer: 3000,
            title: 'Success!',
            text: 'Pending ' + this.$t('request') + ' has been declined.'
          })
        })
        .then(this.handleRedirectToRequest)
    },

    /**
     * Handle redirect to the "requests.show" route after vetting has succeeded.
     *
     * @return void
     */
    handleRedirectToRequest () {
      this.$router.push({ name: 'requests.show', params: { id: this.booking.id } })
    }
  }
}
</script>
